
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  Loader2,
} from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { useLanguage } from "../context/LanguageContext";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

function pickLang(item, language) {
  const translations = item?.translations || {};

  return (
    translations[language] ||
    translations[item?.source_lang] ||
    translations.en ||
    Object.values(translations)[0] ||
    item
  );
}

export default function NewsArticle() {
  const { slug } = useParams();

  const { t, language } = useLanguage();
  const news = t.news || {};

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    if (!slug) {
      setError(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(false);
    setArticle(null);

    api
      .getNewsArticle(slug)
      .then((data) => {
        console.log("NEWS ARTICLE:", data);

        if (!mounted) return;

        setArticle(data);
      })
      .catch((err) => {
        console.error("NEWS ARTICLE ERROR:", err);

        if (!mounted) return;

        setError(true);
      })
      .finally(() => {
        if (!mounted) return;

        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <div
      className="cyber-bg cyber-grid-bg min-h-screen"
      style={{
        backgroundColor: "#050814",
      }}
    >
      <div
        className="glow-orb w-[500px] h-[500px] top-20 right-0 opacity-10"
        style={{
          background:
            "radial-gradient(circle, #0055FF 0%, transparent 70%)",
        }}
      />

      <Navbar />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-32 pb-24">

        {/* Back to News */}
        <Link
          to="/news"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#00F0FF] text-xs terminal transition-colors mb-10"
        >
          <ArrowLeft size={14} />
          {news.backToNews || "Back to News"}
        </Link>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-24 text-[#00F0FF]">
            <Loader2
              size={30}
              className="animate-spin"
            />
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div className="cyber-card p-10 text-center">
            <h1
              className="text-2xl font-bold text-white mb-3"
              style={{
                fontFamily: "Outfit, sans-serif",
              }}
            >
              Article not found
            </h1>

            <p className="text-slate-500 mb-6">
              The news article could not be found.
            </p>

            <Link
              to="/news"
              className="cyber-btn-primary inline-flex"
            >
              <ArrowLeft size={14} />
              {news.backToNews || "Back to News"}
            </Link>
          </div>
        )}

        {/* Article */}
        {!loading && !error && article && (
          <motion.article
            initial={{
              opacity: 0,
              y: 25,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
          >
            {(() => {
              const content = pickLang(
                article,
                language
              );

              return (
                <>
                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-4 mb-6">

                    {article.date && (
                      <div className="flex items-center gap-2 text-slate-500 text-xs">
                        <Calendar size={13} />

                        <span className="terminal">
                          {article.date}
                        </span>
                      </div>
                    )}

                    {content?.tag && (
                      <span className="text-xs px-2 py-1 rounded terminal text-[#00F0FF] bg-[#00F0FF]/10 border border-[#00F0FF]/20">
                        {content.tag}
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h1
                    className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-tight mb-8"
                    style={{
                      fontFamily:
                        "Outfit, sans-serif",
                    }}
                  >
                    {content?.title ||
                      article.title}
                  </h1>

                  {/* Summary */}
                  {(content?.summary ||
                    article.summary) && (
                    <p className="text-xl text-slate-400 leading-relaxed border-l-2 border-[#00F0FF]/40 pl-5 mb-10">
                      {content?.summary ||
                        article.summary}
                    </p>
                  )}

                  {/* Body */}
                  {(content?.body ||
                    article.body) && (
                    <div className="cyber-card p-6 sm:p-10">
                      <div className="text-slate-300 leading-8 whitespace-pre-line text-base">
                        {content?.body ||
                          article.body}
                      </div>
                    </div>
                  )}

                  {/* Original Source */}
                  {article.source_url && (
                    <div className="mt-8">
                      <a
                        href={article.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-slate-500 hover:text-[#00F0FF] terminal transition-colors"
                      >
                        View original source →
                      </a>
                    </div>
                  )}
                </>
              );
            })()}
          </motion.article>
        )}
      </main>

      <Footer />
    </div>
  );
}

